import React from 'react';
import { ToastContext, Dropdown, Modal, Button, Hierarchy, Form, Counter, ToggleContainer, CheckboxLabel, Section } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import useAppContext from '../../../context/useAppContext';
import PropTypes from 'prop-types';
import SessionModel from '../../../models/sessionModel';
import i18n from 'i18next/';
import Styles from './quotasAction.module.css';

export const InputMemo = React.memo(({ id, value, type = 'rate', onChange, blockChange, errors }) => {
    const { t } = useTranslation();
    const timeoutRef = React.useRef(null);

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const setSearch = React.useCallback(
        event => {
            const searchQuery = event.target.value;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                timeoutRef.current = null;
                onChange(event, id, searchQuery, type);
            }, 500);
        },
        [onChange, id, type]
    );

    return (
        <input
            key={`${type}${id}`}
            type="number"
            className={`${Styles['hierarchy__input']} ${errors ? Styles['error'] : ''}`}
            placeholder={t(`ticket.sessions.actions.quotas.placeholderQuota`)}
            defaultValue={value}
            onChange={setSearch}
            onKeyPress={blockChange}
            onKeyDown={blockChange}
        />
    );
});

function HierarchyRates({ rates, rateQuotas, onChangeRate, groupQuotas, onChangeGroup, locale, currency }) {
    const { t } = useTranslation();

    const blockChange = React.useCallback(event => {
        if (event.key === '.' || event.key === ',') {
            event.preventDefault();
            event.stopPropagation();
        }
    }, []);

    const _onChange = React.useCallback(
        (event, id, value, typeObject = 'rate') => {
            if (value && !Number.isInteger(Number(value))) {
                return;
            }

            if (typeObject === 'rate') {
                onChangeRate(currentState => {
                    const newSelected = { ...currentState };
                    newSelected[id] = !value || value === '' ? null : Number(value);

                    return newSelected;
                });
                return;
            }
            onChangeGroup(currentState => {
                const newSelected = { ...currentState };
                newSelected[id] = !value || value === '' ? null : Number(value);

                return newSelected;
            });
        },
        [onChangeGroup, onChangeRate]
    );

    const treeRates = React.useMemo(() => {
        return rates.toOptions({
            extraRate: rate => ({
                component: <InputMemo value={rateQuotas[rate.id] || null} onChange={_onChange} blockChange={blockChange} id={rate.id} />,
                classname: 'row-rate'
            }),
            extraGroup: group => ({
                component: <InputMemo value={groupQuotas[group.id] || null} onChange={_onChange} blockChange={blockChange} id={group.id} type={'group'} />,
                classname: group.parent ? 'row-sub-group' : 'row-group'
            })
        });
    }, [rates, _onChange, rateQuotas, groupQuotas, blockChange]);

    return (
        <div className={Styles['hierarchy']}>
            <Hierarchy
                items={treeRates}
                searchable
                withSelectAll={false}
                placeholderSearch={t('ticket.common.action.search')}
                height={'none'}
                chevron
                depth={0}
                widthIndent={'14px'}
            />
        </div>
    );
}

export function QuotasModal({ items = null, onSubmit = () => {}, onClose = () => {}, loading = false, quota = null, rateQuotas = {}, groupQuotas = {}, rates = null }) {
    const { t } = useTranslation();
    const locale = i18n.language;
    let [{ event }] = useAppContext();
    const [_rateQuotas, setRateQuotas] = React.useState(rateQuotas);
    const [_groupQuotas, setGroupQuotas] = React.useState(groupQuotas);
    const [_quota, setQuota] = React.useState(quota);

    React.useEffect(() => {
        setQuota(quota);
        setRateQuotas(rateQuotas);
        setGroupQuotas(groupQuotas);
    }, [quota, rateQuotas, groupQuotas]);

    const onChangeUnlimitedQuota = React.useCallback((e, { value, checked }) => {
        if (!checked) {
            setQuota(0);
        } else {
            setQuota(null);
        }
    }, []);

    const _onSubmit = React.useCallback(() => {
        onSubmit({ quota: _quota, rate_quotas: _rateQuotas, group_quotas: _groupQuotas });
    }, [_quota, onSubmit, _rateQuotas, _groupQuotas]);

    const hasQuotas = React.useMemo(() => {
        return Boolean(Object.keys(_rateQuotas).length || Object.keys(_groupQuotas).length);
    }, [_rateQuotas, _groupQuotas]);

    if (!rates || !items || items.length === 0) {
        return null;
    }

    return (
        <Modal
            size={'large'}
            open={Boolean(items)}
            onClose={onClose}
            title={t('ticket.sessions.actions.quotas.modalTitle')}
            submitButton={<Button primary disabled={loading} isLoading={loading} onClick={_onSubmit} label={t('ticket.common.action.save')} />}
            cancelButton={<Button disabled={loading} onClick={onClose} label={t('ticket.common.action.cancel')} />}
        >
            <Form.Field>
                <Form.Label>{t(`ticket.sessions.actions.quotas.labelQuota`)}</Form.Label>
                <div className={Styles['container-quota']}>
                    <Counter placeholder={0} onChange={(event, value) => setQuota(value)} disabled={_quota === null} value={_quota} min={0} />
                    <CheckboxLabel
                        onChange={onChangeUnlimitedQuota}
                        checked={_quota === null}
                        label={t(`ticket.sessions.detail.settings.unlimitedQuota`)}
                        name={'unlimited_quota'}
                        value={_quota === null}
                    />
                </div>
                <Form.FieldErrors />
            </Form.Field>

            <ToggleContainer
                className={Styles['quotas-toggle-container']}
                helpLabel={t('ticket.sessions.actions.quotas.refineQuotaHelp')}
                title={t('ticket.sessions.actions.quotas.refineQuota')}
                toggle={hasQuotas}
            >
                <Section>
                    <HierarchyRates
                        rates={rates}
                        locale={locale}
                        currency={event.currency || 'EUR'}
                        rateQuotas={_rateQuotas}
                        onChangeRate={setRateQuotas}
                        groupQuotas={_groupQuotas}
                        onChangeGroup={setGroupQuotas}
                    />
                </Section>
            </ToggleContainer>
        </Modal>
    );
}

QuotasModal.propTypes = {
    items: PropTypes.array,
    onSubmit: PropTypes.func,
    onClose: PropTypes.func,
    loading: PropTypes.bool,
    quota: PropTypes.number,
    ratesQuotas: PropTypes.array,
    onChangeQuotas: PropTypes.func,
    rates: PropTypes.array
};

export function QuotasAction({ objects: selectedSessions, rates = null, onSave = () => {}, loadingRates = false }) {
    const [itemsToAction, setItemsToAction] = React.useState([]);
    const toast = React.useContext(ToastContext);
    const { t } = useTranslation();
    const [{ event, organization }] = useAppContext();

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const itemsFiltered = React.useMemo(() => {
        if (!selectedSessions) {
            return [];
        }
        return selectedSessions;
    }, [selectedSessions]);

    const quota = React.useMemo(() => {
        if (selectedSessions.length !== 1 || itemsFiltered.length !== 1) {
            return null;
        }
        return itemsFiltered[0].quota_total;
    }, [selectedSessions, itemsFiltered]);

    const rateQuotas = React.useMemo(() => {
        if (selectedSessions.length !== 1 || itemsFiltered.length !== 1) {
            return {};
        }

        let _rateQuotas = {};
        if (!selectedSessions || !selectedSessions.length || !itemsFiltered.length) {
            return {};
        }

        itemsFiltered.forEach(session => {
            session.rates.forEach(rate => {
                if (rate.quota) {
                    _rateQuotas[rate.rate_id] = rate.quota.total;
                }
            });
        });

        return _rateQuotas;
    }, [selectedSessions, itemsFiltered]);

    const groupQuotas = React.useMemo(() => {
        if (selectedSessions.length !== 1 || itemsFiltered.length !== 1) {
            return {};
        }

        let _groupQuotas = {};
        if (!selectedSessions || !selectedSessions.length || !itemsFiltered.length) {
            return {};
        }

        itemsFiltered.forEach(session => {
            session.rate_groups?.forEach(group => {
                if (group.quota) {
                    _groupQuotas[group.id] = group.quota.total;
                }
            });
        });

        return _groupQuotas;
    }, [selectedSessions, itemsFiltered]);

    const handleSubmit = React.useCallback(
        ({ quota, rate_quotas, group_quotas }) => {
            setIsSubmitting(true);
            SessionModel.perform_action('quotas', itemsToAction, {
                organization,
                event,
                quota,
                rate_quotas,
                group_quotas
            })
                .then(() => {
                    toast.success(t(`ticket.sessions.actions.quotas.success`, { count: itemsFiltered.length }));
                    onSave();
                    setItemsToAction(null);
                })
                .catch(e => {
                    console.error(e);
                    toast.error(t(`ticket.sessions.actions.quotas.error`, { count: itemsFiltered.length }));
                    setItemsToAction(null);
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
        },
        [event, organization, SessionModel, toast, itemsToAction, onSave, itemsFiltered]
    );

    if (!rates || loadingRates) {
        return null;
    }

    return (
        <>
            <QuotasModal
                items={itemsToAction}
                rates={rates}
                onClose={() => setItemsToAction(null)}
                onSubmit={handleSubmit}
                totalItemsSelected={selectedSessions.length}
                loading={isSubmitting}
                quota={quota}
                rateQuotas={rateQuotas}
                groupQuotas={groupQuotas}
            />
            <Dropdown.Item disabled={!itemsFiltered.length} onClick={() => setItemsToAction(itemsFiltered.length > 0 ? itemsFiltered : null)}>
                {t('ticket.sessions.actions.quotas.label')}
            </Dropdown.Item>
        </>
    );
}

QuotasAction.propTypes = {
    objects: PropTypes.array.isRequired, //required
    rates: PropTypes.array,
    onSave: PropTypes.func
};
